"use client"

import { useCallback, useEffect, useRef, useState } from "react"

import { runWhenPageVisible } from "@/lib/analytics"

/**
 * Fire a visibility-gated impression at most once per mount and once per key.
 *
 * The ordering here is the whole point. `runWhenPageVisible` resolves
 * immediately on a visible page but *pends* on a hidden one (background tab,
 * SERP prerender) and hands back a cleanup that cancels the pending callback.
 * A dedupe key claimed before the callback runs therefore records intent, not
 * delivery: the next dep change (an id prop that lands a render later) cancels
 * the pending impression, and the already-claimed key blocks the effect from
 * re-arming, so the event can never fire again for that attempt.
 *
 * Claiming inside the callback keeps the key meaning "this was delivered".
 * The per-mount ref carries the other half of the contract: ids threaded from
 * refs settle a render late, so one mounted surface must still emit exactly
 * one impression even though its key changed underneath it.
 */
export function useVisibleImpression(
  registry: Set<string>,
  enabled: boolean,
  key: string,
  emit: () => void,
) {
  const emitRef = useRef(emit)
  emitRef.current = emit
  const deliveredRef = useRef(false)

  useEffect(() => {
    if (!enabled || deliveredRef.current || registry.has(key)) return

    return runWhenPageVisible(() => {
      if (deliveredRef.current || registry.has(key)) return
      deliveredRef.current = true
      registry.add(key)
      emitRef.current()
    })
  }, [registry, enabled, key])
}

/**
 * Viewport-gated sibling of `useVisibleImpression`, for an offer that sits far
 * below the fold. Returns a ref callback: attach it to every element that can
 * wrap the offer (branches render more than one, and a copy hidden at the
 * current breakpoint must not be the one that decides), and the impression is
 * claimed the first time any of them intersects.
 *
 * Page visibility still has the final say — the intersection only arms the same
 * `runWhenPageVisible` handoff — so a prerendered or backgrounded document
 * keeps the pending-then-cancel semantics documented above.
 */
export function useInViewportImpression(
  registry: Set<string>,
  enabled: boolean,
  key: string,
  emit: () => void,
): (node: Element | null) => void {
  const emitRef = useRef(emit)
  const deliveredKeyRef = useRef<string | null>(null)
  const nodesRef = useRef<Set<Element>>(new Set())
  const [nodeVersion, setNodeVersion] = useState(0)

  useEffect(() => {
    emitRef.current = emit
  }, [emit])

  // React hands back `null` when a node detaches. Ignoring it left the observer
  // pointed at an element no longer in the document once the last wrapper
  // unmounted with no replacement, so the impression could never be claimed
  // again. The version bump re-runs the effect, which sweeps the set after the
  // DOM removal has committed and re-observes whatever is left.
  const attach = useCallback((node: Element | null) => {
    if (node === null) {
      setNodeVersion((version) => version + 1)
      return
    }
    if (nodesRef.current.has(node)) return
    nodesRef.current.add(node)
    setNodeVersion((version) => version + 1)
  }, [])

  useEffect(() => {
    if (!enabled || deliveredKeyRef.current === key || registry.has(key)) return

    const claim = () => {
      if (deliveredKeyRef.current === key || registry.has(key)) return
      deliveredKeyRef.current = key
      registry.add(key)
      emitRef.current()
    }

    // No observer, no gate. Falling silent instead would drop the impression
    // entirely, which costs the funnel its denominator; the surrounding
    // impressions take the same fallback.
    if (typeof IntersectionObserver === 'undefined') return runWhenPageVisible(claim)

    for (const node of nodesRef.current) {
      if (!node.isConnected) nodesRef.current.delete(node)
    }
    const nodes = Array.from(nodesRef.current)
    if (nodes.length === 0) return

    let cancelPending: (() => void) | null = null
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      observer.disconnect()
      cancelPending = runWhenPageVisible(claim)
    })
    nodes.forEach((node) => observer.observe(node))

    return () => {
      observer.disconnect()
      cancelPending?.()
    }
  }, [registry, enabled, key, nodeVersion])

  return attach
}
